import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesServicioAPI, ordenesClientesAPI, ordenesVehiculosAPI, ordenesCatalogosAPI } from '../../lib/ordenesApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
    ArrowLeft,
    ClipboardList,
    Car,
    Users,
    Loader2,
    Plus,
    Search,
} from 'lucide-react';

export default function OrdenFormPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const preselectedClienteId = searchParams.get('cliente_id');
    
    const { tienePermiso, loading: permisosLoading } = useOrdenesPermisos();
    const [saving, setSaving] = useState(false);
    
    // Data
    const [clientes, setClientes] = useState([]);
    const [vehiculos, setVehiculos] = useState([]);
    const [tiposServicio, setTiposServicio] = useState([]);
    
    // Form
    const [clienteId, setClienteId] = useState(preselectedClienteId || '');
    const [vehiculoId, setVehiculoId] = useState('');
    const [vehiculoOption, setVehiculoOption] = useState('existing'); // existing | new
    const [tipoServicio, setTipoServicio] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [fechaProgramada, setFechaProgramada] = useState('');
    const [prioridad, setPrioridad] = useState('media');
    const [observaciones, setObservaciones] = useState('');
    
    // Nuevo vehículo
    const [nuevoVehiculo, setNuevoVehiculo] = useState({
        patente: '',
        tipo_vehiculo: '',
        marca: '',
        modelo: '',
        anio: '',
        color: '',
        vin: '',
    });

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (clienteId) {
            loadVehiculos(clienteId);
        } else {
            setVehiculos([]);
            setVehiculoId('');
        }
    }, [clienteId]);

    const loadInitialData = async () => {
        try {
            const [clientesRes, tiposRes] = await Promise.all([
                ordenesClientesAPI.getAll(),
                ordenesCatalogosAPI.getTiposServicio(),
            ]);
            setClientes(clientesRes.data);
            setTiposServicio(tiposRes.data);
        } catch (err) {
            console.error('Error loading initial data:', err);
        }
    };

    const loadVehiculos = async (cId) => {
        try {
            const res = await ordenesVehiculosAPI.getAll({ cliente_id: cId });
            setVehiculos(res.data);
        } catch (err) {
            console.error('Error loading vehiculos:', err);
        }
    };

    const handleSubmit = async () => {
        if (!clienteId) {
            toast.error('Debe seleccionar un cliente');
            return;
        }
        if (!tipoServicio) {
            toast.error('Debe seleccionar un tipo de servicio');
            return;
        }
        if (vehiculoOption === 'new' && !nuevoVehiculo.patente) {
            toast.error('Debe ingresar la patente del vehículo');
            return;
        }

        setSaving(true);
        try {
            const data = {
                cliente_id: clienteId,
                tipo_servicio: tipoServicio,
                descripcion,
                fecha_programada: fechaProgramada || null,
                prioridad,
                observaciones,
            };

            if (vehiculoOption === 'existing' && vehiculoId) {
                data.vehiculo_id = vehiculoId;
            } else if (vehiculoOption === 'new') {
                data.vehiculo_patente = nuevoVehiculo.patente;
                data.vehiculo_tipo = nuevoVehiculo.tipo_vehiculo;
                data.vehiculo_marca = nuevoVehiculo.marca;
                data.vehiculo_modelo = nuevoVehiculo.modelo;
                data.vehiculo_anio = nuevoVehiculo.anio ? parseInt(nuevoVehiculo.anio) : null;
                data.vehiculo_color = nuevoVehiculo.color;
                data.vehiculo_vin = nuevoVehiculo.vin;
            }

            const res = await ordenesServicioAPI.create(data);
            toast.success(`Orden ${res.data.numero_orden} creada exitosamente`);
            navigate(`/ordenes/ordenes/${res.data.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al crear la orden');
        } finally {
            setSaving(false);
        }
    };

    if (permisosLoading) {
        return <div className="p-6"><Loader2 className="h-8 w-8 animate-spin" /></div>;
    }

    return (
        <div className="space-y-6 p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/ordenes')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <ClipboardList className="h-6 w-6" />
                        Nueva Orden de Servicio
                    </h1>
                    <p className="text-muted-foreground">Complete los datos para crear una nueva orden</p>
                </div>
            </div>

            {/* Cliente */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Cliente
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Seleccionar Cliente *</Label>
                            <Select value={clienteId} onValueChange={setClienteId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccione un cliente" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clientes.map(c => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.razon_social} ({c.rut})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => navigate('/ordenes/clientes')}>
                            <Plus className="h-4 w-4 mr-2" />
                            Crear Nuevo Cliente
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Vehículo */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Car className="h-5 w-5" />
                        Vehículo
                    </CardTitle>
                    <CardDescription>
                        Seleccione un vehículo existente o ingrese los datos de uno nuevo
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <RadioGroup value={vehiculoOption} onValueChange={setVehiculoOption}>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="existing" id="existing" />
                                <Label htmlFor="existing">Vehículo existente del cliente</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="new" id="new" />
                                <Label htmlFor="new">Registrar nuevo vehículo</Label>
                            </div>
                        </RadioGroup>

                        {vehiculoOption === 'existing' && (
                            <div className="space-y-2">
                                <Label>Seleccionar Vehículo</Label>
                                <Select value={vehiculoId} onValueChange={setVehiculoId} disabled={!clienteId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={clienteId ? "Seleccione un vehículo" : "Primero seleccione un cliente"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {vehiculos.map(v => (
                                            <SelectItem key={v.id} value={v.id}>
                                                {v.patente} - {v.marca} {v.modelo}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {vehiculos.length === 0 && clienteId && (
                                    <p className="text-sm text-muted-foreground">Este cliente no tiene vehículos registrados</p>
                                )}
                            </div>
                        )}

                        {vehiculoOption === 'new' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                                <div className="space-y-2">
                                    <Label>Patente *</Label>
                                    <Input
                                        value={nuevoVehiculo.patente}
                                        onChange={(e) => setNuevoVehiculo({...nuevoVehiculo, patente: e.target.value.toUpperCase()})}
                                        placeholder="AA-BB-12"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Tipo de Vehículo</Label>
                                    <Select 
                                        value={nuevoVehiculo.tipo_vehiculo} 
                                        onValueChange={(v) => setNuevoVehiculo({...nuevoVehiculo, tipo_vehiculo: v})}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Seleccione" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="auto">Automóvil</SelectItem>
                                            <SelectItem value="camioneta">Camioneta</SelectItem>
                                            <SelectItem value="camion">Camión</SelectItem>
                                            <SelectItem value="bus">Bus</SelectItem>
                                            <SelectItem value="moto">Motocicleta</SelectItem>
                                            <SelectItem value="maquinaria">Maquinaria</SelectItem>
                                            <SelectItem value="otro">Otro</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Marca</Label>
                                    <Input
                                        value={nuevoVehiculo.marca}
                                        onChange={(e) => setNuevoVehiculo({...nuevoVehiculo, marca: e.target.value})}
                                        placeholder="Toyota, Volvo, etc."
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Modelo</Label>
                                    <Input
                                        value={nuevoVehiculo.modelo}
                                        onChange={(e) => setNuevoVehiculo({...nuevoVehiculo, modelo: e.target.value})}
                                        placeholder="Hilux, FH16, etc."
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Año</Label>
                                    <Input
                                        type="number"
                                        value={nuevoVehiculo.anio}
                                        onChange={(e) => setNuevoVehiculo({...nuevoVehiculo, anio: e.target.value})}
                                        placeholder="2024"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Color</Label>
                                    <Input
                                        value={nuevoVehiculo.color}
                                        onChange={(e) => setNuevoVehiculo({...nuevoVehiculo, color: e.target.value})}
                                        placeholder="Blanco, Rojo, etc."
                                    />
                                </div>
                                <div className="space-y-2 sm:col-span-2">
                                    <Label>VIN / N° Chasis</Label>
                                    <Input
                                        value={nuevoVehiculo.vin}
                                        onChange={(e) => setNuevoVehiculo({...nuevoVehiculo, vin: e.target.value})}
                                        placeholder="Número de chasis"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Servicio */}
            <Card>
                <CardHeader>
                    <CardTitle>Detalles del Servicio</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Tipo de Servicio *</Label>
                                <Select value={tipoServicio} onValueChange={setTipoServicio}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Seleccione" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {tiposServicio.map(t => (
                                            <SelectItem key={t.codigo} value={t.codigo}>{t.nombre}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Prioridad</Label>
                                <Select value={prioridad} onValueChange={setPrioridad}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="alta">Alta</SelectItem>
                                        <SelectItem value="media">Media</SelectItem>
                                        <SelectItem value="baja">Baja</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Fecha Programada</Label>
                            <Input
                                type="date"
                                value={fechaProgramada}
                                onChange={(e) => setFechaProgramada(e.target.value)}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Descripción del Trabajo</Label>
                            <Textarea
                                value={descripcion}
                                onChange={(e) => setDescripcion(e.target.value)}
                                placeholder="Describa el trabajo a realizar..."
                                rows={3}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Observaciones</Label>
                            <Textarea
                                value={observaciones}
                                onChange={(e) => setObservaciones(e.target.value)}
                                placeholder="Observaciones adicionales..."
                                rows={2}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-4">
                <Button variant="outline" onClick={() => navigate('/ordenes/ordenes')} disabled={saving}>
                    Cancelar
                </Button>
                <Button onClick={handleSubmit} disabled={saving}>
                    {saving ? (
                        <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Creando...
                        </>
                    ) : (
                        'Crear Orden'
                    )}
                </Button>
            </div>
        </div>
    );
}
